import { isOnlineReservationTable } from './reservation-tables';
import type { IReservationSetupTable } from './reservation-tables';

/** The fields of a table combination that decide who it can seat. */
export interface IReservationCapacityGroup {
  tableIds: readonly number[];
  minParty: number;
  maxParty: number;
}

export interface IOnlineCoverageBand {
  /** Party sizes covered by this band, inclusive. */
  from: number;
  to: number;
  /** Single tables that seat any party in the band. */
  tables: number;
  /** Combinations that seat any party in the band. */
  groups: number;
}

export interface IOnlineCoverage {
  /** Largest party a guest can book online; 0 when nothing is bookable. */
  maxParty: number;
  /** Consecutive party sizes between the online limits with the same options. */
  bands: IOnlineCoverageBand[];
}

interface ICapacityTable extends IReservationSetupTable {
  id: number;
}

/**
 * Mirrors the API's candidate rule for guests: a single table needs
 * `capacity >= party`; a combination needs every member online (a member
 * without seats still counts), at least two members, and the party inside
 * its own range and total seats.
 */
function onlineTables(tables: readonly ICapacityTable[]): ICapacityTable[] {
  return tables.filter(isOnlineReservationTable);
}

/** Seats a combination offers online, or 0 when it can never be offered. */
function groupSeats(
  group: IReservationCapacityGroup,
  online: readonly ICapacityTable[]
): number {
  const ids = new Set(group.tableIds);
  if (ids.size < 2 || ids.size !== group.tableIds.length) return 0;
  const members = group.tableIds
    .map((id) => online.find((table) => table.id === id))
    .filter((table): table is ICapacityTable => table !== undefined);
  if (members.length !== group.tableIds.length) return 0;
  const seats = Math.min(
    group.maxParty,
    members.reduce((total, table) => total + table.capacity, 0)
  );
  // A range that starts above the seats is never offered to anyone.
  return seats >= group.minParty ? seats : 0;
}

/** Largest party any online table or combination can seat. */
export function maxBookableOnlineParty(
  tables: readonly ICapacityTable[],
  groups: readonly IReservationCapacityGroup[]
): number {
  const online = onlineTables(tables);
  return Math.max(
    0,
    ...online.map((table) => table.capacity),
    ...groups.map((group) => groupSeats(group, online))
  );
}

/** Diner rule (cartadigital-app reservations.utils.ts effectiveMaxParty). */
export function effectiveOnlineMaxParty(
  minParty: number,
  maxParty: number,
  bookable: number
): number {
  return bookable >= Math.max(1, minParty)
    ? Math.min(maxParty, bookable)
    : maxParty;
}

/**
 * Which party sizes between the online limits have a table, and how many.
 * Bands merge neighbouring sizes with identical options so the owner reads
 * "1–2 people: 9 tables" instead of one line per size.
 */
export function onlineCoverage(
  tables: readonly ICapacityTable[],
  groups: readonly IReservationCapacityGroup[],
  minParty: number,
  maxParty: number
): IOnlineCoverage {
  const online = onlineTables(tables);
  const seats = groups.map((group) => ({
    group,
    seats: groupSeats(group, online),
  }));
  const bands: IOnlineCoverageBand[] = [];
  for (let party = Math.max(1, minParty); party <= maxParty; party += 1) {
    const tablesFor = online.filter((table) => table.capacity >= party).length;
    const groupsFor = seats.filter(
      ({ group, seats: total }) =>
        total >= party && party >= group.minParty && party <= group.maxParty
    ).length;
    const last = bands[bands.length - 1];
    if (last && last.tables === tablesFor && last.groups === groupsFor)
      last.to = party;
    else
      bands.push({
        from: party,
        to: party,
        tables: tablesFor,
        groups: groupsFor,
      });
  }
  return { maxParty: maxBookableOnlineParty(tables, groups), bands };
}
